import filter from 'lodash/filter.js';
import flatten from 'lodash/flatten.js';
import get from 'lodash/get.js';
import intersectionBy from 'lodash/intersectionBy.js';
import map from 'lodash/map.js';
import numeral from 'numeral';

import TimePeriodRangeData from '@/components/attendance/TimePeriodRangeData.js';
import Course from '@/models/Course.js';
import EnrollmentGroupTimePeriod from '@/models/EnrollmentGroupTimePeriod.js';
import Model from '@/models/Model.js';
import moment from '@/plugins/vue-moment.js';
import QueryBuilder from '@/utilities/QueryBuilder.js';

export default class TimePeriod extends Model {
    static entity = 'time_periods';

    static modelName = 'TimePeriod';

    static fields() {
        return {
            id: this.attr(null),
            course_id: this.attr(null),
            section_id: this.attr(null),
            external_id: this.attr(null),
            course_key_id: this.attr(null),
            check_in_code: this.attr(null),
            title: this.attr(null),
            from: this.attr(null),
            to: this.attr(null),
            window_from: this.attr(null),
            window_to: this.attr(null),
            credit_time: this.number(null),
            clock_time: this.number(null),
            break_time: this.number(null),
            considered_present_percent: this.number(0),
            students_can_see_status: this.boolean(false),
            is_for_course_key: this.boolean(true),
            expected_time: this.attr(null),

            ...super.fields(),

            course: this.belongsTo('courses', 'course_id'),
            section: this.belongsTo('sections', 'section_id'),
            satisfiers: this.hasMany('satisfiers', 'time_period_id'),
            locations: this.belongsToMany('locations', 'time_period_location', 'location_id', 'time_period_id'),
            enrollment_groups: this.belongsToMany(
                'enrollment_groups',
                'enrollment_group_time_period',
                'time_period_id',
                'enrollment_group_id'
            ),
            enrollments: this.belongsToMany('users', 'time_period_enrollment', 'user_id', 'time_period_id').as(
                'enrollment'
            ),
            attendances: this.hasMany('attendances', 'time_period_id'),
        };
    }

    static beforeUpdate(model) {
        // Clear existing ModelRole pivot entries to avoid duplicates (vuex-orm bug)
        EnrollmentGroupTimePeriod.query()
            .where('time_period_id', model.id)
            .get()
            .forEach(pivotModel => pivotModel.$delete());
    }

    get range_data() {
        return new TimePeriodRangeData(this);
    }

    get scheduled_time() {
        const total_minutes = Math.max(
            this.timestamp('to').diff(this.timestamp('from'), 'minutes') - this.break_time,
            0
        );
        const hours = Math.floor(total_minutes / 60);

        return {
            total_minutes,
            hours,
            minutes: total_minutes - hours * 60,
            break_minutes: this.break_time,
        };
    }

    get duration() {
        return numeral(this.timestamp('to').diff(this.timestamp('from'), 'hours', true)).format('0.0');
    }

    get duration_seconds() {
        return this.timestamp('to').diff(this.timestamp('from'), 'seconds', true);
    }

    get tooltip() {
        return `${this.timestamp('from').format('LLL zz')} - ${this.timestamp('to').format('LLL zz')}`;
    }

    get has_group() {
        return this.enrollment_groups.length > 0;
    }

    group(attribute, defaultValue = null) {
        return get(this, `enrollment_groups[0].${attribute}`, defaultValue);
    }

    get has_issue() {
        const course = this.course || Course.query().with('course_settings').find(this.course_id);

        if (!course) {
            throw new Error("Could not load the time period's associated course.");
        }

        if (course.course_settings.course_key_enabled && course.has_instructor) {
            if (this.enrollment_groups.length) {
                return this.enrollment_groups.filter(enrollmentGroup => !enrollmentGroup.has_issue).length === 0;
            }

            if (!this.is_for_course_key) {
                return false;
            }

            return !this.check_in_code || !this.course_key_id;
        }

        return false;
    }

    get range_display() {
        const to = this.timestamp('from').isSame(this.timestamp('to'), 'day')
            ? this.timestamp('to').format('h:mm a')
            : this.timestamp('to').format('ddd, M/D h:mm a');

        return `${this.timestamp('from').format('ddd, M/D h:mm a')} - ${to}`;
    }

    get range_simple() {
        const from = this.timestamp('from');
        const to = this.timestamp('to');

        if (from.day() !== to.day()) {
            return `${from.format('ddd h:mm a')} - ${to.format('ddd h:mm a')}`;
        }

        return `${from.format('h:mm a')} - ${to.format('h:mm a')}`;
    }

    get is_active() {
        return moment().isBetween(this.timestamp('window_from'), this.timestamp('window_to'));
    }

    get is_in_future() {
        return moment().isBefore(this.timestamp('window_from'));
    }

    get is_in_past() {
        return moment().isAfter(this.timestamp('window_to'));
    }

    isOverlapped(prospectiveTimeRanges) {
        return (
            filter(
                prospectiveTimeRanges.map(([prospectiveFrom, prospectiveTo, timePeriodId]) => {
                    const from = this.timestamp('from');
                    const to = this.timestamp('to');

                    return (
                        (this.id !== timePeriodId &&
                            prospectiveFrom.isSameOrAfter(from) &&
                            prospectiveFrom.isBefore(to)) ||
                        (prospectiveTo.isAfter(from) && prospectiveTo.isBefore(to)) ||
                        (prospectiveFrom.isSameOrBefore(from) && prospectiveTo.isSameOrAfter(to))
                    );
                })
            ).length > 0
        );
    }

    static overlappedTimes({ selectedSectionIds, prospectiveTimeRanges, excludeId, enrollmentGroupId }) {
        return TimePeriod.query()
            .with('enrollment_groups')
            .where(
                timePeriod =>
                    selectedSectionIds.includes(timePeriod.section_id) &&
                    timePeriod.id !== excludeId &&
                    timePeriod.isOverlapped(prospectiveTimeRanges)
            )
            .get()
            .filter(timePeriod => {
                if (!timePeriod.enrollment_groups.length && !enrollmentGroupId) {
                    return true;
                }

                return map(timePeriod.enrollment_groups, 'id').includes(enrollmentGroupId);
            });
    }

    get style() {
        return this.enrollment_groups.length ? this.enrollment_groups[0].style : {};
    }

    get can_be_modified() {
        return !this.external_id && !this.is_active && !this.is_in_past;
    }

    enrolledUserIsInGroup(enrollment) {
        if (this.enrollment_groups.length === 0) {
            return true;
        }

        if (!enrollment) {
            return false;
        }

        return intersectionBy(enrollment.enrollment_groups, this.enrollment_groups, 'id').length > 0;
    }

    static async fetchForAttendanceScheduler(courseId) {
        const queryBuilder = new QueryBuilder('time-periods')
            .include(['locations', 'enrollments', 'enrollmentGroups.color', 'satisfiers'])
            .filter('course_id', courseId)
            .pageSize('all');

        const { response } = await TimePeriod.api().get(queryBuilder.toUrl());

        const pivots = map(flatten(map(response.data.data, 'enrollment_groups')), 'pivot');

        EnrollmentGroupTimePeriod.deleteAll();

        // Manually inserted pivot data
        EnrollmentGroupTimePeriod.insert({ data: pivots });
    }

    static searchable() {
        return ['course'];
    }
}
